import React, { useMemo } from 'react'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { IconAdd, IconCategory, IconSearch, IconTemplate } from '../../../../../shared/icon/icon'
import { Route, useRouteMatch } from '../../../../../../node_modules/react-router-dom/cjs/react-router-dom.min'
import { useParamsInt } from '../../../../../shared/hooks/location'
import {
  useApiFormNodeTemplate,
  useApiFormNodeTemplateCategory,
  useApiListNodeDecendantTemplate,
  useApiListNodeTemplateCategory
} from '../../../../../api-new/state/content'
import { NodeTemplateGridItem } from './node-template-grid-item'
import { NodeTemplateDetails } from './pages/node-template-details'
import { CardFullHeightScrollY } from '../../../../../shared/card/card-full-height-scroll-y'
import { FOLDER_SECTION, FolderMenu } from '../common/menu/node-actions'
import { NodeCategoryGridItem } from '../node-category/node-category-grid-item'
import { NodeTemplateAdd } from './pages/node-template-add'
import { NodeCategory } from '../node-category/node-category'
import { ListError } from '../common/search/list-error'
import { platformLabel } from '../common/platform/platforms'
import { formatLabel, keepFormat } from '../common/format/formats'
import { useFormatFilter } from '../common/format/format-controls'
import { NodeSearchArea } from '../common/search/node-search-area'
import { TEMPLATE_SEARCH, hasFormat } from '../common/search/search-areas'
import { buildTemplateCategoryPath } from '../common/shared/build-path'
import { DataProvider, useDataContext } from './data-context'
import { NodeGridBody } from '../common/search/node-grid-body'
import { useLoginSysAdmin } from '../../../../../role/hook/login-sys-admin'
import { useRoleOrgNode } from '../../../../../role/hook/role-org'

export const NodeTemplateGrid = () => {
  return (
    <DataProvider>
      <div className="container-fluid p-0 h-100">
        <NodeTemplateGridBranch />
      </div>
    </DataProvider>
  )
}

const NodeTemplateGridBranch = ({ grandParentURL }) => {
  const { url } = useRouteMatch()
  const { parentID } = useParamsInt()

  const dataContext = useDataContext()

  const category = useMemo(() => dataContext.categories.find(i => i.id === parentID), [dataContext, parentID])

  // "Available on": with a platform chosen, this level shows content's
  // answer for it -- the templates available there and the folders above
  // them -- so a folder holding one, at any depth, can still be opened.
  const platform = dataContext.platform || null
  const platformView = platform && dataContext.filtered ? dataContext.filtered : dataContext
  const loaded = platform && dataContext.filtered ? dataContext.filtered.loaded : dataContext.loaded

  // "Format" ("v2:", ...): that format's templates and the folders above
  // them, from whichever list is shown.
  // Super admins only (owner 2026-10-11; format-controls.js): for anyone
  // else there is no format, whatever the area holds.
  const { format, setFormat } = useFormatFilter(dataContext.format, dataContext.setFormat)
  const shownItems = platform && dataContext.filtered ? dataContext.filtered.items : dataContext.items
  const view = useMemo(
    () => {
      if (!format) return platformView
      const keep = new Set(keepFormat(shownItems || [], format, hasFormat(TEMPLATE_SEARCH)).map(node => node.id))
      return {
        categories: platformView.categories.filter(node => keep.has(node.id)),
        templates: platformView.templates.filter(node => keep.has(node.id))
      }
    },
    [platformView, format, shownItems]
  )

  const categories = useMemo(
    () => {
      if (parentID) return view.categories.filter(i => i.parentID === parentID)
      else return view.categories.filter(i => i.parentID === null)
    },
    [view, parentID]
  )

  const templates = useMemo(
    () => {
      if (parentID) return view.templates.filter(i => i.parentID === parentID)
      else return view.templates.filter(i => i.parentID === null)
    },
    [view, parentID]
  )

  const title = useMemo(() => (category ? category.name : 'TEMPLATES'), [category])

  const Icon = useMemo(
    () => {
      if (parentID) return IconCategory
      else return IconTemplate
    },
    [parentID]
  )

  const HeaderMenu = () => <FolderMenu place="header" url={url} nodeID={parentID} section={FOLDER_SECTION.TEMPLATE} />

  // Zero state. Templates are made by saving a course as a template, so the
  // way forward is Content; sys admins also get the header menu's "add node".
  const loginSysAdmin = useLoginSysAdmin()
  const roleOrgNode = useRoleOrgNode()

  const isEmpty = categories.length === 0 && templates.length === 0

  const platformName = platformLabel(platform)
  const showAllPlatforms = () => dataContext.setPlatform && dataContext.setPlatform(null)
  const formatName = formatLabel(format)
  const showEveryFormat = () => setFormat && setFormat(null)

  const emptyState = format && !platform
    ? {
        Icon: IconSearch,
        title: parentID ? `Nothing here is ${formatName}` : `No ${formatName} templates`,
        message: parentID ? `No template in this category is ${formatName}.` : `No template in this organization is ${formatName}.`,
        action: { label: 'Show every format', onClick: showEveryFormat }
      }
    : platform
    ? {
        Icon: IconSearch,
        title: parentID ? `Nothing here runs on ${platformName}` : `No templates run on ${platformName}`,
        message: parentID ? `No template in this category runs on ${platformName} yet.` : `No template in this organization runs on ${platformName} yet.`,
        action: { label: 'Show all platforms', onClick: showAllPlatforms }
      }
    : {
        Icon: parentID ? IconCategory : IconTemplate,
        title: parentID ? 'This category is empty' : 'No templates yet',
        message: parentID ? 'Templates you add to this category appear here.' : 'Save a course as a template to reuse it.',
        action: { label: 'Add template', Icon: IconAdd, to: `${url}/add-type`, show: loginSysAdmin },
        secondary: { label: 'Go to content', to: '/org/node-content', show: !parentID && !!roleOrgNode.accessRead }
      }

  const filterError = platform && dataContext.filtered ? dataContext.filtered.error : undefined

  return (
    <React.Fragment>
      <BreadcrumbView Icon={Icon} text={title}>
        <CardFullHeightScrollY title={title} HeaderMenu={HeaderMenu}>
          <NodeSearchArea
            area={TEMPLATE_SEARCH}
            nodes={dataContext.items}
            thumbnails={dataContext.thumbnails}
            items={shownItems}
            loaded={loaded}
            error={platform && dataContext.filtered ? filterError : dataContext.error}
            onRetry={platform && dataContext.filtered ? dataContext.filtered.retry : dataContext.retry}
            platform={platform}
            onPlatformChange={dataContext.setPlatform}
            format={format}
            onFormatChange={setFormat}>
            {filterError
              ? <ListError message={`Couldn't load the templates that run on ${platformName}.`} error={filterError} onRetry={dataContext.filtered.retry} />
              : <NodeGridBody loaded={loaded} isEmpty={isEmpty} emptyState={emptyState}>
                  <div className="container-fluid">
                    <div className="node-grid">
                      {categories
                        .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
                        .map((node, i) => <NodeCategoryGridItem key={i} node={node} dataContext={dataContext} section={FOLDER_SECTION.TEMPLATE} />)}

                      {templates
                        .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
                        .map((node, i) => <NodeTemplateGridItem key={i} node={node} dataContext={dataContext} />)}
                    </div>
                  </div>
                </NodeGridBody>}
          </NodeSearchArea>
        </CardFullHeightScrollY>
      </BreadcrumbView>
      <Route path={`${url}/node/:parentID`}>
        <NodeTemplateGridBranch grandParentURL={url} />
      </Route>
      <Route path={`${url}/template/:nodeID`}>
        <NodeTemplateDetails parentURL={url} />
      </Route>

      <Route path={`${url}/add-type`}>
        <NodeTemplateAdd parentURL={url} parentID={parentID} />
      </Route>

      <NodeCategory
        url={url}
        grandParentURL={grandParentURL}
        nodeID={parentID}
        rootName="TEMPLATE"
        useApiListCategory={useApiListNodeTemplateCategory}
        useApiFormCategory={useApiFormNodeTemplateCategory}
        useApiListRecycle={useApiListNodeDecendantTemplate}
        useApiFormRecycle={useApiFormNodeTemplate}
        buildPathFn={buildTemplateCategoryPath}
        dataContext={dataContext}
        section={FOLDER_SECTION.TEMPLATE}
      />
    </React.Fragment>
  )
}
